<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">
      <div class="settings-grid">

        <!-- ── Project info ─────────────────────────────────────────── -->
        <Panel>
          <template #header>
            <span class="panel-title">PROJECT</span>
            <div class="header-actions">
              <span class="adapter-badge">{{ project.rag_adapter }}</span>
              <button v-if="canEdit" class="action-btn" @click="openEditProject">EDIT</button>
            </div>
          </template>
          <div class="info-section">
            <div class="info-row">
              <span class="info-label">NAME</span>
              <span class="info-value">{{ project.name }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">UUID</span>
              <span class="info-value mono">{{ project.uuid }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">DESCRIPTION</span>
              <span class="info-value desc">{{ project.description || '—' }}</span>
            </div>
          </div>
        </Panel>

        <!-- ── AI Clients ────────────────────────────────────────────── -->
        <Panel>
          <template #header>
            <span class="panel-title">AI CLIENTS</span>
            <div class="header-actions">
              <span class="panel-meta">{{ ai_clients.length }} assigned</span>
              <button v-if="canEdit" class="action-btn" @click="openAssignClient">+ ASSIGN</button>
            </div>
          </template>
          <div v-if="ai_clients.length === 0" class="empty-row">
            No AI clients assigned.
          </div>
          <div v-for="a in ai_clients" :key="a.id" class="list-row">
            <div class="row-left">
              <span class="provider-dot" :style="{ background: providerColor(a.ai_client.provider) }" />
              <div class="client-info">
                <span class="client-name">{{ a.ai_client.name }}</span>
                <span class="client-model">{{ a.ai_client.model }}</span>
              </div>
            </div>
            <div class="badges-right">
              <span class="purpose-badge">{{ a.purpose }}</span>
              <span v-if="a.is_default" class="default-badge">DEFAULT</span>
              <button v-if="canEdit" class="remove-btn" title="Remove assignment" @click="confirmRemoveClient(a)">✕</button>
            </div>
          </div>
        </Panel>

        <!-- ── Members ───────────────────────────────────────────────── -->
        <Panel>
          <template #header>
            <span class="panel-title">MEMBERS</span>
            <span class="panel-meta">{{ members.length }} members</span>
          </template>
          <div v-for="m in members" :key="m.id" class="list-row">
            <div class="row-left">
              <div class="avatar">{{ initials(m.user.name) }}</div>
              <div class="member-info">
                <span class="member-name">{{ m.user.name }}</span>
                <span class="member-email">{{ m.user.email }}</span>
              </div>
            </div>
            <span class="role-badge" :class="m.role">{{ m.role }}</span>
          </div>
        </Panel>

        <!-- ── Structured Output Schema ──────────────────────────────── -->
        <Panel v-if="canEdit">
          <template #header>
            <span class="panel-title">STRUCTURED OUTPUT</span>
            <div class="header-actions">
              <span v-if="project.output_schema" class="active-badge">ACTIVE</span>
              <span v-else class="panel-meta">optional</span>
            </div>
          </template>

          <div class="schema-section">
            <p class="schema-hint">
              Force every pipeline response to match a JSON schema. The pipeline will retry up to
              <strong>max retries</strong> times to produce valid output.
            </p>

            <div class="schema-toolbar">
              <label class="field-label">JSON SCHEMA</label>
              <button
                class="suggest-btn"
                :disabled="schemaSuggesting"
                @click="suggestSchema"
              >{{ schemaSuggesting ? 'ASKING AI…' : '✦ SUGGEST WITH AI' }}</button>
            </div>
            <textarea
              v-model="schemaJson"
              class="schema-editor"
              placeholder='{"matched_id": "integer", "name": "string", "confidence": "number"}'
              spellcheck="false"
              rows="6"
            />
            <p v-if="schemaParseError" class="field-error">{{ schemaParseError }}</p>
            <p class="schema-types-hint">
              Allowed types: <code>string</code> <code>integer</code> <code>number</code>
              <code>boolean</code> <code>array</code> <code>object</code>
            </p>

            <label class="field-label" style="margin-top:14px">MAX RETRIES</label>
            <select v-model="schemaMaxRetries" class="field-input field-select schema-retries">
              <option v-for="n in [1,2,3,4,5]" :key="n" :value="n">{{ n }} {{ n === 1 ? 'retry' : 'retries' }}</option>
            </select>

            <p v-if="schemaError" class="field-error">{{ schemaError }}</p>
            <p v-if="schemaSaved" class="field-success">Schema saved.</p>

            <div class="schema-actions">
              <button
                v-if="project.output_schema"
                class="btn-ghost danger"
                :disabled="schemaSaving"
                @click="clearSchema"
              >CLEAR SCHEMA</button>
              <button
                class="btn-primary"
                :disabled="schemaSaving"
                @click="saveSchema"
              >{{ schemaSaving ? 'SAVING…' : 'SAVE SCHEMA' }}</button>
            </div>
          </div>
        </Panel>

        <!-- ── Eval Score Threshold ─────────────────────────────────── -->
        <Panel v-if="canEdit">
          <template #header>
            <span class="panel-title">EVAL THRESHOLD</span>
            <span class="panel-meta">auto-trigger orchestrator</span>
          </template>
          <div class="schema-section">
            <p class="schema-hint">
              When an eval score falls below the threshold, the orchestrator auto-triggers.
              When unused corrections reach the feedback trigger, an eval auto-runs.
              Set either to blank to disable it.
            </p>

            <label class="field-label">SCORE THRESHOLD (%)</label>
            <input
              v-model.number="thresholdValue"
              class="field-input threshold-input"
              type="number"
              min="0"
              max="100"
              step="1"
              placeholder="70"
            />

            <label class="field-label" style="margin-top:14px">FEEDBACK TRIGGER (corrections)</label>
            <input
              v-model.number="feedbackTriggerValue"
              class="field-input threshold-input"
              type="number"
              min="1"
              step="1"
              placeholder="10"
            />
            <p class="field-hint">Auto-run eval after this many unused corrections accumulate.</p>

            <p v-if="thresholdError" class="field-error">{{ thresholdError }}</p>
            <p v-if="thresholdSaved" class="field-success">Settings saved.</p>
            <div class="schema-actions">
              <button
                class="btn-primary"
                :disabled="thresholdSaving"
                @click="saveThreshold"
              >{{ thresholdSaving ? 'SAVING…' : 'SAVE SETTINGS' }}</button>
            </div>
          </div>
        </Panel>

        <!-- ── API Keys ──────────────────────────────────────────────── -->
        <Panel v-if="api_keys.length > 0 || user_role === 'owner'">
          <template #header>
            <span class="panel-title">API KEYS</span>
            <div class="header-actions">
              <span class="panel-meta">{{ api_keys.length }} keys</span>
              <button v-if="user_role === 'owner'" class="action-btn" @click="openCreateKey">+ CREATE KEY</button>
            </div>
          </template>
          <div v-if="api_keys.length === 0" class="empty-row">
            No API keys yet.
          </div>
          <div v-for="k in api_keys" :key="k.id" class="list-row">
            <div class="row-left">
              <div class="key-info">
                <span class="key-name">{{ k.name }}</span>
                <span class="key-prefix mono">{{ k.key_prefix }}••••</span>
              </div>
            </div>
            <div class="key-meta-right">
              <span v-if="k.revoked_at" class="revoked-badge">REVOKED</span>
              <span class="key-user">{{ k.user.name }}</span>
              <span class="key-date">{{ formatDate(k.last_used_at) || 'never used' }}</span>
              <button v-if="!k.revoked_at && user_role === 'owner'" class="remove-btn danger" title="Revoke key" @click="confirmRevokeKey(k)">REVOKE</button>
            </div>
          </div>
        </Panel>
      </div>
    </main>

    <!-- ── Edit Project Modal ───────────────────────────────────────── -->
    <div v-if="editProjectOpen" class="modal-backdrop" @click.self="editProjectOpen = false">
      <div class="modal">
        <div class="modal-header">
          <span class="modal-title">EDIT PROJECT</span>
          <button class="modal-close" @click="editProjectOpen = false">✕</button>
        </div>
        <div class="modal-body">
          <label class="field-label">NAME</label>
          <input v-model="editForm.name" class="field-input" type="text" placeholder="Project name" />
          <label class="field-label" style="margin-top:14px">DESCRIPTION</label>
          <textarea v-model="editForm.description" class="field-input field-textarea" placeholder="What does this project do?" />
          <p v-if="editError" class="field-error">{{ editError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="editProjectOpen = false">CANCEL</button>
          <button class="btn-primary" :disabled="editSaving" @click="saveProject">
            {{ editSaving ? 'SAVING…' : 'SAVE CHANGES' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Assign AI Client Modal ───────────────────────────────────── -->
    <div v-if="assignClientOpen" class="modal-backdrop" @click.self="assignClientOpen = false">
      <div class="modal">
        <div class="modal-header">
          <span class="modal-title">ASSIGN AI CLIENT</span>
          <button class="modal-close" @click="assignClientOpen = false">✕</button>
        </div>
        <div class="modal-body">
          <label class="field-label">AI CLIENT</label>
          <select v-model="assignForm.ai_client_id" class="field-input field-select">
            <option value="" disabled>Select a client…</option>
            <option v-for="c in available_ai_clients" :key="c.id" :value="c.id">
              {{ c.name }} — {{ c.model }}
            </option>
          </select>
          <label class="field-label" style="margin-top:14px">PURPOSE</label>
          <select v-model="assignForm.purpose" class="field-input field-select">
            <option value="main">main — primary pipeline execution</option>
            <option value="critique">critique — feedback loop reviewer</option>
            <option value="orchestrator">orchestrator — self-improvement cycle</option>
          </select>
          <label class="field-label checkbox-row" style="margin-top:14px">
            <input v-model="assignForm.is_default" type="checkbox" class="field-checkbox" />
            <span>Set as default for this purpose</span>
          </label>
          <p v-if="assignError" class="field-error">{{ assignError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="assignClientOpen = false">CANCEL</button>
          <button class="btn-primary" :disabled="assignSaving || !assignForm.ai_client_id" @click="saveAssignment">
            {{ assignSaving ? 'ASSIGNING…' : 'ASSIGN CLIENT' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Remove AI Client Confirm ─────────────────────────────────── -->
    <div v-if="removeClientTarget" class="modal-backdrop" @click.self="removeClientTarget = null">
      <div class="modal modal-sm">
        <div class="modal-header">
          <span class="modal-title">REMOVE ASSIGNMENT</span>
          <button class="modal-close" @click="removeClientTarget = null">✕</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">
            Remove <strong>{{ removeClientTarget?.ai_client?.name }}</strong>
            ({{ removeClientTarget?.purpose }}) from this project?
          </p>
          <p v-if="removeClientError" class="field-error">{{ removeClientError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="removeClientTarget = null">CANCEL</button>
          <button class="btn-danger" :disabled="removingClient" @click="removeAssignment">
            {{ removingClient ? 'REMOVING…' : 'REMOVE' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Create API Key Modal ─────────────────────────────────────── -->
    <div v-if="createKeyOpen" class="modal-backdrop" @click.self="closeCreateKey">
      <div class="modal">
        <div class="modal-header">
          <span class="modal-title">{{ newKeyValue ? 'COPY YOUR KEY' : 'CREATE API KEY' }}</span>
          <button class="modal-close" @click="closeCreateKey">✕</button>
        </div>

        <!-- Step 1: enter name -->
        <template v-if="!newKeyValue">
          <div class="modal-body">
            <label class="field-label">KEY NAME</label>
            <input v-model="keyForm.name" class="field-input" type="text" placeholder="e.g. Production server" />
            <p class="field-hint">Give this key a name so you can identify it later.</p>
            <p v-if="keyError" class="field-error">{{ keyError }}</p>
          </div>
          <div class="modal-footer">
            <button class="btn-ghost" @click="closeCreateKey">CANCEL</button>
            <button class="btn-primary" :disabled="keySaving || !keyForm.name.trim()" @click="createApiKey">
              {{ keySaving ? 'CREATING…' : 'CREATE KEY' }}
            </button>
          </div>
        </template>

        <!-- Step 2: show raw key (once only) -->
        <template v-else>
          <div class="modal-body">
            <p class="key-warning">⚠ Copy this key now — it will never be shown again.</p>
            <div class="key-reveal">
              <span class="key-reveal-text">{{ newKeyValue }}</span>
              <button class="copy-btn" @click="copyKey">{{ copied ? 'COPIED ✓' : 'COPY' }}</button>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-primary" @click="closeCreateKey">DONE</button>
          </div>
        </template>
      </div>
    </div>

    <!-- ── Revoke Key Confirm ────────────────────────────────────────── -->
    <div v-if="revokeKeyTarget" class="modal-backdrop" @click.self="revokeKeyTarget = null">
      <div class="modal modal-sm">
        <div class="modal-header">
          <span class="modal-title">REVOKE KEY</span>
          <button class="modal-close" @click="revokeKeyTarget = null">✕</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">
            Revoke <strong>{{ revokeKeyTarget?.name }}</strong>?
            Any services using it will stop working immediately.
          </p>
          <p v-if="revokeKeyError" class="field-error">{{ revokeKeyError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="revokeKeyTarget = null">CANCEL</button>
          <button class="btn-danger" :disabled="revokingKey" @click="revokeKey">
            {{ revokingKey ? 'REVOKING…' : 'REVOKE KEY' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import Panel from "../../components/Panel.vue"

const props = defineProps({
  project:              { type: Object, required: true },
  members:              { type: Array,  default: () => [] },
  api_keys:             { type: Array,  default: () => [] },
  ai_clients:           { type: Array,  default: () => [] },
  available_ai_clients: { type: Array,  default: () => [] },
  user_role:            { type: String, default: "viewer" },
})

// ── Eval Settings (score threshold + feedback trigger) ───────────────────────
const thresholdValue      = ref(props.project.eval_score_threshold ?? 70)
const feedbackTriggerValue = ref(props.project.feedback_eval_trigger ?? 10)
const thresholdSaving     = ref(false)
const thresholdError      = ref("")
const thresholdSaved      = ref(false)

async function saveThreshold() {
  const score    = Number(thresholdValue.value)
  const feedback = feedbackTriggerValue.value === "" || feedbackTriggerValue.value == null
    ? null
    : Number(feedbackTriggerValue.value)

  if (isNaN(score) || score < 0 || score > 100) {
    thresholdError.value = "Score threshold must be between 0 and 100."
    return
  }
  if (feedback !== null && (isNaN(feedback) || feedback < 1)) {
    thresholdError.value = "Feedback trigger must be at least 1."
    return
  }
  thresholdSaving.value = true
  thresholdError.value  = ""
  thresholdSaved.value  = false
  try {
    const body = { eval_score_threshold: score, feedback_eval_trigger: feedback }
    const { status, data } = await apiCall("PATCH", `/api/v1/projects/${props.project.uuid}`, body)
    if (status === 200) { thresholdSaved.value = true; setTimeout(() => { thresholdSaved.value = false }, 3000) }
    else thresholdError.value = data?.message || "Failed to save."
  } catch { thresholdError.value = "Network error." }
  finally { thresholdSaving.value = false }
}

const canEdit = computed(() => ["owner", "editor"].includes(props.user_role))

// ── helpers ─────────────────────────────────────────────────────────────────
function initials(name) {
  return (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
}
function providerColor(provider) {
  const map = { openai: "#4ade80", anthropic: "#60a5fa", google: "#c084fc" }
  return map[provider?.toLowerCase()] ?? "#f5a623"
}
function formatDate(dateStr) {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
}
async function apiCall(method, path, body = null) {
  const token = document.querySelector("meta[name='csrf-token']")?.content
  const opts = { method, headers: { "Content-Type": "application/json", "X-CSRF-Token": token } }
  if (body) opts.body = JSON.stringify(body)
  const res = await fetch(path, opts)
  return { status: res.status, data: await res.json() }
}
function reload() {
  router.visit(`/projects/${props.project.uuid}/settings`, { replace: true })
}

// ── Structured Output Schema ─────────────────────────────────────────────────
const schemaJson        = ref(props.project.output_schema ? JSON.stringify(props.project.output_schema, null, 2) : "")
const schemaMaxRetries  = ref(props.project.output_schema_max_retries ?? 3)
const schemaSaving      = ref(false)
const schemaSuggesting  = ref(false)
const schemaError       = ref("")
const schemaParseError  = ref("")
const schemaSaved       = ref(false)

function parseSchema() {
  const raw = schemaJson.value.trim()
  if (!raw) return null
  try {
    schemaParseError.value = ""
    return JSON.parse(raw)
  } catch {
    schemaParseError.value = "Invalid JSON — check your schema syntax."
    return undefined  // undefined = parse error (null = intentionally empty)
  }
}
async function saveSchema() {
  const parsed = parseSchema()
  if (parsed === undefined) return
  schemaSaving.value = true
  schemaError.value  = ""
  schemaSaved.value  = false
  try {
    const body = { output_schema: parsed, output_schema_max_retries: schemaMaxRetries.value }
    const { status, data } = await apiCall("PATCH", `/api/v1/projects/${props.project.uuid}/output_schema`, body)
    if (status === 200) { schemaSaved.value = true; setTimeout(() => { schemaSaved.value = false }, 3000) }
    else schemaError.value = data?.errors?.join(", ") || data?.error || "Failed to save."
  } catch { schemaError.value = "Network error." }
  finally { schemaSaving.value = false }
}
async function clearSchema() {
  schemaSaving.value = true
  schemaError.value  = ""
  try {
    const { status, data } = await apiCall("PATCH", `/api/v1/projects/${props.project.uuid}/output_schema`, { output_schema: null })
    if (status === 200) { schemaJson.value = ""; schemaSaved.value = true; setTimeout(() => { schemaSaved.value = false }, 3000) }
    else schemaError.value = data?.error || "Failed to clear."
  } catch { schemaError.value = "Network error." }
  finally { schemaSaving.value = false }
}
async function suggestSchema() {
  schemaSuggesting.value = true
  schemaError.value = ""
  try {
    const { status, data } = await apiCall("POST", `/api/v1/projects/${props.project.uuid}/output_schema/suggest`)
    if (status === 200) { schemaJson.value = JSON.stringify(data.suggested_schema, null, 2) }
    else schemaError.value = data?.error || "AI suggestion failed."
  } catch { schemaError.value = "Network error." }
  finally { schemaSuggesting.value = false }
}

// ── Edit Project ─────────────────────────────────────────────────────────────
const editProjectOpen = ref(false)
const editSaving      = ref(false)
const editError       = ref("")
const editForm        = ref({ name: "", description: "" })

function openEditProject() {
  editForm.value = { name: props.project.name, description: props.project.description || "" }
  editError.value = ""
  editProjectOpen.value = true
}
async function saveProject() {
  if (!editForm.value.name.trim()) { editError.value = "Name is required."; return }
  editSaving.value = true
  editError.value  = ""
  try {
    const { status, data } = await apiCall("PATCH", `/api/v1/projects/${props.project.uuid}`, editForm.value)
    if (status === 200) { editProjectOpen.value = false; reload() }
    else editError.value = data?.message || "Failed to save."
  } catch { editError.value = "Network error." }
  finally { editSaving.value = false }
}

// ── Assign AI Client ─────────────────────────────────────────────────────────
const assignClientOpen = ref(false)
const assignSaving     = ref(false)
const assignError      = ref("")
const assignForm       = ref({ ai_client_id: "", purpose: "main", is_default: false })

function openAssignClient() {
  assignForm.value  = { ai_client_id: "", purpose: "main", is_default: false }
  assignError.value = ""
  assignClientOpen.value = true
}
async function saveAssignment() {
  assignSaving.value = true
  assignError.value  = ""
  try {
    const { status, data } = await apiCall("POST", `/api/v1/projects/${props.project.uuid}/ai_clients`, assignForm.value)
    if (status === 201) { assignClientOpen.value = false; reload() }
    else assignError.value = data?.message || "Failed to assign."
  } catch { assignError.value = "Network error." }
  finally { assignSaving.value = false }
}

// ── Remove AI Client ─────────────────────────────────────────────────────────
const removeClientTarget = ref(null)
const removingClient     = ref(false)
const removeClientError  = ref("")

function confirmRemoveClient(assignment) {
  removeClientTarget.value = assignment
  removeClientError.value  = ""
}
async function removeAssignment() {
  removingClient.value    = true
  removeClientError.value = ""
  try {
    const { status, data } = await apiCall("DELETE", `/api/v1/projects/${props.project.uuid}/ai_clients/${removeClientTarget.value.id}`)
    if (status === 200) { removeClientTarget.value = null; reload() }
    else removeClientError.value = data?.message || "Failed to remove."
  } catch { removeClientError.value = "Network error." }
  finally { removingClient.value = false }
}

// ── Create API Key ───────────────────────────────────────────────────────────
const createKeyOpen = ref(false)
const keySaving     = ref(false)
const keyError      = ref("")
const keyForm       = ref({ name: "" })
const newKeyValue   = ref("")
const copied        = ref(false)

function openCreateKey() {
  keyForm.value    = { name: "" }
  keyError.value   = ""
  newKeyValue.value = ""
  copied.value     = false
  createKeyOpen.value = true
}
function closeCreateKey() {
  createKeyOpen.value = false
  if (newKeyValue.value) reload()
}
async function createApiKey() {
  if (!keyForm.value.name.trim()) { keyError.value = "Name is required."; return }
  keySaving.value = true
  keyError.value  = ""
  try {
    const { status, data } = await apiCall("POST", `/api/v1/projects/${props.project.uuid}/api_keys`, keyForm.value)
    if (status === 201) { newKeyValue.value = data?.api_key?.key || "" }
    else keyError.value = data?.message || "Failed to create key."
  } catch { keyError.value = "Network error." }
  finally { keySaving.value = false }
}
async function copyKey() {
  try { await navigator.clipboard.writeText(newKeyValue.value); copied.value = true } catch {}
}

// ── Revoke API Key ───────────────────────────────────────────────────────────
const revokeKeyTarget = ref(null)
const revokingKey     = ref(false)
const revokeKeyError  = ref("")

function confirmRevokeKey(key) {
  revokeKeyTarget.value = key
  revokeKeyError.value  = ""
}
async function revokeKey() {
  revokingKey.value    = true
  revokeKeyError.value = ""
  try {
    const { status, data } = await apiCall("DELETE", `/api/v1/projects/${props.project.uuid}/api_keys/${revokeKeyTarget.value.id}`)
    if (status === 200) { revokeKeyTarget.value = null; reload() }
    else revokeKeyError.value = data?.message || "Failed to revoke."
  } catch { revokeKeyError.value = "Network error." }
  finally { revokingKey.value = false }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}
.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}
.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
}
.settings-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* ── Panel header elements ── */
.panel-title {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(232, 230, 227, 0.45);
}
.panel-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.adapter-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 3px;
  background: rgba(245, 166, 35, 0.18);
  color: #f5a623;
}
.action-btn {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 3px 9px;
  border-radius: 3px;
  border: 1px solid rgba(245, 166, 35, 0.35);
  background: rgba(245, 166, 35, 0.08);
  color: #f5a623;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.action-btn:hover {
  background: rgba(245, 166, 35, 0.16);
  border-color: rgba(245, 166, 35, 0.6);
}

/* ── Info section ── */
.info-section { padding: 14px 18px; }
.info-row { display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px; }
.info-row:last-child { margin-bottom: 0; }
.info-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: rgba(232, 230, 227, 0.25);
}
.info-value { font-family: var(--font-body); font-size: 13px; color: #e8e6e3; }
.info-value.mono { font-family: var(--font-mono); font-size: 11px; color: rgba(232, 230, 227, 0.6); }
.info-value.desc { font-size: 12px; color: rgba(232, 230, 227, 0.6); line-height: 1.5; }

/* ── List rows ── */
.empty-row {
  padding: 24px 18px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.25);
  text-align: center;
}
.list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  gap: 12px;
  transition: background 0.15s;
}
.list-row:hover { background: rgba(245, 166, 35, 0.04); }
.row-left { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; }
.provider-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.client-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.client-name {
  font-family: var(--font-mono); font-size: 12px; font-weight: 600; color: #e8e6e3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.client-model { font-family: var(--font-mono); font-size: 10px; color: rgba(232, 230, 227, 0.25); }
.badges-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.purpose-badge {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.8px; text-transform: uppercase;
  padding: 2px 7px; border-radius: 3px;
  background: rgba(96, 165, 250, 0.15); color: #60a5fa;
}
.default-badge {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.8px; text-transform: uppercase;
  padding: 2px 7px; border-radius: 3px;
  background: rgba(74, 222, 128, 0.15); color: #4ade80;
}
.remove-btn {
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  padding: 2px 7px; border-radius: 3px; border: 1px solid transparent;
  background: transparent; color: rgba(248, 113, 113, 0.4);
  cursor: pointer; transition: color 0.15s, border-color 0.15s, background 0.15s;
  flex-shrink: 0;
}
.remove-btn:hover { color: #f87171; border-color: rgba(248, 113, 113, 0.35); background: rgba(248, 113, 113, 0.08); }
.remove-btn.danger {
  font-size: 9px; letter-spacing: 0.6px; color: rgba(248, 113, 113, 0.5);
}
.remove-btn.danger:hover { color: #f87171; border-color: rgba(248, 113, 113, 0.35); background: rgba(248, 113, 113, 0.08); }

/* ── Member ── */
.avatar {
  width: 30px; height: 30px; border-radius: 50%;
  background: rgba(245, 166, 35, 0.18); color: #f5a623;
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.member-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.member-name { font-family: var(--font-mono); font-size: 12px; font-weight: 600; color: #e8e6e3; }
.member-email {
  font-family: var(--font-mono); font-size: 10px; color: rgba(232, 230, 227, 0.25);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.role-badge {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.8px; text-transform: uppercase;
  padding: 2px 7px; border-radius: 3px; flex-shrink: 0;
}
.role-badge.owner  { background: rgba(245, 166, 35, 0.18);  color: #f5a623; }
.role-badge.editor { background: rgba(96, 165, 250, 0.15);  color: #60a5fa; }
.role-badge.viewer { background: rgba(255, 255, 255, 0.06); color: rgba(232, 230, 227, 0.45); }

/* ── API Keys ── */
.key-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.key-name { font-family: var(--font-mono); font-size: 12px; font-weight: 600; color: #e8e6e3; }
.key-prefix { font-family: var(--font-mono); font-size: 10px; color: rgba(232, 230, 227, 0.25); }
.key-meta-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.revoked-badge {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700; letter-spacing: 0.8px;
  padding: 2px 7px; border-radius: 3px; background: rgba(248, 113, 113, 0.15); color: #f87171;
}
.key-user { font-family: var(--font-mono); font-size: 11px; color: rgba(232, 230, 227, 0.45); }
.key-date { font-family: var(--font-mono); font-size: 10px; color: rgba(232, 230, 227, 0.25); }

/* ── Modal ── */
.modal-backdrop {
  position: fixed; inset: 0; z-index: 50;
  background: rgba(0, 0, 0, 0.72);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.modal {
  background: #141416;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
}
.modal.modal-sm { max-width: 360px; }
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.modal-title {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 1.2px; color: rgba(232, 230, 227, 0.6);
}
.modal-close {
  background: none; border: none; color: rgba(232, 230, 227, 0.3);
  font-size: 14px; cursor: pointer; padding: 0; line-height: 1;
  transition: color 0.15s;
}
.modal-close:hover { color: #e8e6e3; }
.modal-body { padding: 20px; display: flex; flex-direction: column; }
.modal-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex; justify-content: flex-end; gap: 10px;
}
.field-label {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1.2px; color: rgba(232, 230, 227, 0.35);
  margin-bottom: 6px; display: block;
}
.field-input {
  width: 100%; box-sizing: border-box;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  padding: 9px 12px;
  font-family: var(--font-mono); font-size: 12px; color: #e8e6e3;
  outline: none; transition: border-color 0.15s;
}
.field-input:focus { border-color: rgba(245, 166, 35, 0.5); }
.field-textarea { resize: vertical; min-height: 80px; }
.field-select { appearance: none; cursor: pointer; }
.checkbox-row { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.field-checkbox { width: 14px; height: 14px; cursor: pointer; accent-color: #f5a623; flex-shrink: 0; }
.field-hint {
  font-family: var(--font-mono); font-size: 10px;
  color: rgba(232, 230, 227, 0.25); margin-top: 6px;
}
.field-error {
  font-family: var(--font-mono); font-size: 10px; color: #f87171; margin-top: 8px;
}
.confirm-text {
  font-family: var(--font-body); font-size: 13px;
  color: rgba(232, 230, 227, 0.7); line-height: 1.5;
}
.confirm-text strong { color: #e8e6e3; }

/* ── Key reveal ── */
.key-warning {
  font-family: var(--font-mono); font-size: 11px;
  color: #f5a623; margin-bottom: 14px;
}
.key-reveal {
  display: flex; align-items: stretch; gap: 0;
  border: 1px solid rgba(245, 166, 35, 0.3); border-radius: 5px; overflow: hidden;
}
.key-reveal-text {
  flex: 1; padding: 10px 12px;
  font-family: var(--font-mono); font-size: 11px;
  color: #e8e6e3; word-break: break-all;
  background: rgba(245, 166, 35, 0.05);
}
.copy-btn {
  padding: 10px 14px;
  background: rgba(245, 166, 35, 0.15); border: none;
  border-left: 1px solid rgba(245, 166, 35, 0.3);
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  letter-spacing: 0.6px; color: #f5a623; cursor: pointer;
  transition: background 0.15s; white-space: nowrap;
}
.copy-btn:hover { background: rgba(245, 166, 35, 0.25); }

/* ── Buttons ── */
.btn-primary {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px; border: none;
  background: #f5a623; color: #0a0a0b; cursor: pointer;
  transition: opacity 0.15s;
}
.btn-primary:hover:not(:disabled) { opacity: 0.88; }
.btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-ghost {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent; color: rgba(232, 230, 227, 0.45); cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}
.btn-ghost:hover { border-color: rgba(255, 255, 255, 0.2); color: rgba(232, 230, 227, 0.7); }
.btn-danger {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px; border: none;
  background: rgba(248, 113, 113, 0.15); color: #f87171; cursor: pointer;
  border: 1px solid rgba(248, 113, 113, 0.3);
  transition: background 0.15s;
}
.btn-danger:hover:not(:disabled) { background: rgba(248, 113, 113, 0.25); }
.btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-ghost.danger { color: rgba(248, 113, 113, 0.5); border-color: rgba(248, 113, 113, 0.2); }
.btn-ghost.danger:hover { color: #f87171; border-color: rgba(248, 113, 113, 0.4); background: rgba(248, 113, 113, 0.06); }

/* ── Structured Output Schema ── */
.active-badge {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.8px; text-transform: uppercase;
  padding: 2px 7px; border-radius: 3px;
  background: rgba(74, 222, 128, 0.15); color: #4ade80;
}
.schema-section { padding: 16px 18px; display: flex; flex-direction: column; }
.schema-hint {
  font-family: var(--font-mono); font-size: 11px;
  color: rgba(232, 230, 227, 0.4); margin: 0 0 14px; line-height: 1.6;
}
.schema-hint strong { color: rgba(232, 230, 227, 0.6); }
.schema-toolbar {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;
}
.suggest-btn {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.6px; padding: 3px 10px; border-radius: 3px;
  border: 1px solid rgba(192, 132, 252, 0.3);
  background: rgba(192, 132, 252, 0.08); color: #c084fc;
  cursor: pointer; transition: background 0.15s, border-color 0.15s;
}
.suggest-btn:hover:not(:disabled) { background: rgba(192, 132, 252, 0.15); border-color: rgba(192, 132, 252, 0.5); }
.suggest-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.schema-editor {
  width: 100%; box-sizing: border-box; resize: vertical;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px; padding: 10px 12px;
  font-family: var(--font-mono); font-size: 11px; color: #e8e6e3;
  outline: none; line-height: 1.6; transition: border-color 0.15s; margin-bottom: 4px;
}
.schema-editor:focus { border-color: rgba(245, 166, 35, 0.4); }
.schema-editor::placeholder { color: rgba(232, 230, 227, 0.15); }
.schema-types-hint {
  font-family: var(--font-mono); font-size: 10px;
  color: rgba(232, 230, 227, 0.25); margin: 0; line-height: 2;
}
.schema-types-hint code {
  background: rgba(255, 255, 255, 0.06); color: rgba(232, 230, 227, 0.5);
  padding: 1px 5px; border-radius: 3px; margin-right: 2px;
}
.schema-retries { margin-top: 6px; }
.threshold-input { margin-top: 6px; max-width: 120px; }
.schema-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
.field-success {
  font-family: var(--font-mono); font-size: 10px; color: #4ade80; margin-top: 8px;
}
</style>
